import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import { useNavigate } from "react-router-dom";
import DynamicSignature from "app/components/Individual/signature/DynamicSignature";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";

interface FormData {
  terms: boolean;
}

const DeclarationSignaturesPartOne: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { applicantID, ...savedIds } = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const navigate = useNavigate();
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const { mutate: uploadImages } = useAddAssetsLiabilitiesImages();
  const sigCanvas = useRef<SignatureCanvas>(null);
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const { mutate: uploadS3 } = useS3Upload();
  // Function to clear the signature canvas
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const saveSignature = () => {
    if (sigCanvas.current) {
      const signatureData = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
    }
  };

  const dataURLtoFile = (dataUrl: string, filename: string) => {
    let arr = dataUrl.split(","),
      mime = arr[0].match(/:(.*?);/)![1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<FormData>();
  const termsChecked = watch("terms");

  const onSubmit = (data: FormData) => {
    if (!applicantID) {
      toast.error("Applicant ID is missing.");
      return;
    }
    if (!sigCanvas.current) {
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setSignatureError("Digital signature is required.");
      return;
    } else {
      setSignatureError(null);
    }

    const signatureData = sigCanvas?.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    const signatureFile = dataURLtoFile(signatureData, "signature.png");
    setIsLoading(true);
    getPresignedUrls(
      {
        url: `/document/${applicantID}/presigned-urls`,
        data: { documentTypes: ["passport"] },
      },
      {
        onSuccess: (presignedData) => {
          const key = presignedData?.passport?.key;
          const uploadUrl = presignedData?.passport?.url;

          if (!key || !uploadUrl) {
            setIsLoading(false);
            toast.error("Failed to get pre-signed URL for signature image.");
            return;
          }
          uploadS3(
            {
              url: uploadUrl,
              data: signatureFile,
            },
            {
              onSuccess: (data: any) => {
                if (data === 200) {
                  const file = {
                    signature: {
                      key: key,
                      fileName: signatureFile.name,
                      mimeType: signatureFile.type,
                    },
                  };
                  uploadImages(
                    {
                      url: `/document/${applicantID}/signature`,
                      data: file,
                    },
                    {
                      onSuccess: (data: any) => {
                        setIsLoading(false);
                        // toast.success("Application submitted successfully!");
                        sigCanvas.current?.clear();
                        navigate("/success");
                      },
                      onError: (error: any) => {
                        setIsLoading(false);
                        toast.error(
                          error?.response?.data?.message ||
                            "something went wrong."
                        );
                      },
                    }
                  );
                }
              },
            }
          );
        },
      }
    );
  };

  const handlePrevious = () => {
    if (!applicantID) return;
    navigate(`/assets-liabilities`);
  };

  return (
    <>
      <div className="w-full sm:w-[60%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Declaration & Consent " />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <DynamicSignature
            sigCanvas={sigCanvas}
            clearSignature={clearSignature}
            signatureError={signatureError}
            setSignatureError={setSignatureError}
            control={control}
            id="sigCanvas"
            terms="terms"
          />
          <div className="flex justify-between my-10">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton
              type="submit"
              loading={isLoading}
              disabled={!termsChecked}
            >
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default DeclarationSignaturesPartOne;
